import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { S5Query, S5Record, S5TaxType, S5TaxStatus } from "types";
import { formatCurrency } from "utils/helpers/accounting";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const startOfYear = `${today.getFullYear()}-01-01`;
const todayString = formatDate(today);

const defaultFilters: S5Query = {
  from_date: startOfYear,
  to_date: todayString,
};

// Tax type labels for UI
const TAX_TYPE_LABELS: Record<S5TaxType, string> = {
  VAT: "Thuế GTGT",
  PIT: "Thuế TNCN",
  OTHER: "Thuế khác",
};

// Status labels for UI
const STATUS_LABELS: Record<S5TaxStatus, string> = {
  SUFFICIENT: "Đủ",
  DEFICIT: "Thiếu",
  SURPLUS: "Thừa",
};

const S5Report: React.FC = () => {
  const { fetchS5Hkd } = useReport();
  const [filters, setFilters] = useState<S5Query>(defaultFilters);
  const [submitted, setSubmitted] = useState<S5Query | null>(defaultFilters);

  useEffect(() => {
    fetchS5Hkd.mutate(defaultFilters);
  }, [fetchS5Hkd.mutate]);

  const handleFilterChange = useCallback((key: keyof S5Query, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) {
        return;
      }
      setSubmitted(filters);
      fetchS5Hkd.mutate(filters);
    },
    [fetchS5Hkd, filters],
  );

  // Get data from API response
  const responseData = fetchS5Hkd.data?.data;
  const filteredRecords = (responseData?.records ?? []) as S5Record[];
  const apiSummary = responseData?.summary;

  const summary = useMemo(() => {
    if (apiSummary) {
      return apiSummary;
    }
    // Fallback to calculating from records
    const vatRecords = filteredRecords.filter((r) => r.tax_type === "VAT");
    const pitRecords = filteredRecords.filter((r) => r.tax_type === "PIT");

    return {
      vat_payable: vatRecords.reduce((sum, r) => sum + (r.tax_payable || 0), 0),
      vat_paid: vatRecords.reduce((sum, r) => sum + (r.tax_paid || 0), 0),
      vat_remaining: vatRecords.reduce(
        (sum, r) => sum + (r.tax_remaining || 0),
        0,
      ),
      pit_payable: pitRecords.reduce((sum, r) => sum + (r.tax_payable || 0), 0),
      pit_paid: pitRecords.reduce((sum, r) => sum + (r.tax_paid || 0), 0),
      pit_remaining: pitRecords.reduce(
        (sum, r) => sum + (r.tax_remaining || 0),
        0,
      ),
    };
  }, [apiSummary, filteredRecords]);

  const getStatusBadgeClass = (status: S5TaxStatus) => {
    switch (status) {
      case "SUFFICIENT":
        return "bg-green-100 text-green-800 border-green-200";
      case "DEFICIT":
        return "bg-red-100 text-red-800 border-red-200";
      case "SURPLUS":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const columns = useMemo(
    () => [
      {
        headerName: "Kỳ tính thuế",
        field: "period",
        pinned: "left",
        minWidth: 120,
        valueFormatter: (params: any) => {
          const period = params.value;
          if (period.includes("Q")) return period;
          const [year, month] = period.split("-");
          return `Tháng ${parseInt(month)}/${year}`;
        },
      },
      {
        headerName: "Loại thuế",
        field: "tax_type",
        pinned: "left",
        minWidth: 120,
        valueFormatter: (params: any) =>
          TAX_TYPE_LABELS[params.value as S5TaxType] || params.value,
      },
      {
        headerName: "Doanh thu tính thuế",
        field: "taxable_revenue",
        minWidth: 160,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? formatCurrency(Number(params.value))
            : "",
        cellClass: "text-right font-medium text-gray-700",
      },
      {
        headerName: "Thuế phải nộp",
        field: "tax_payable",
        minWidth: 160,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? formatCurrency(Number(params.value))
            : "",
        cellClass: "text-right font-semibold text-gray-900 bg-gray-50",
        headerTooltip: "Tự động tính từ S1 - Không được nhập tay",
      },
      {
        headerName: "Thuế đã nộp",
        field: "tax_paid",
        minWidth: 160,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? formatCurrency(Number(params.value))
            : "",
        cellClass: "text-right font-semibold text-blue-700",
      },
      {
        headerName: "Còn phải nộp",
        field: "tax_remaining",
        minWidth: 160,
        valueFormatter: (params: any) => {
          const value = Number(params.value);
          if (value === 0) return "0 ₫";
          return formatCurrency(value);
        },
        cellClass: (params: any) => {
          const value = Number(params.value);
          if (value > 0) return "text-right font-bold text-red-700";
          if (value < 0) return "text-right font-bold text-blue-700";
          return "text-right font-bold text-green-700";
        },
      },
      {
        headerName: "Trạng thái",
        field: "status",
        minWidth: 100,
        cellRenderer: (params: any) => {
          const status = params.value as S5TaxStatus;
          const badgeClass = getStatusBadgeClass(status);
          return (
            <span className={`px-2 py-1 text-xs font-semibold rounded border ${badgeClass}`}>
              {STATUS_LABELS[status]}
            </span>
          );
        },
      },
      {
        headerName: "Ghi chú",
        field: "note",
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: any) => params.value || "",
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      minWidth: 100,
    }),
    [],
  );

  const isLoading = fetchS5Hkd.isPending;

  return (
    <div className="p-6 space-y-6">
      {isLoading && (
        <LoadingState message="Đang tải báo cáo" description="Vui lòng chờ trong giây lát." />
      )}
      <div>
        <p className="text-sm text-gray-500 uppercase">Báo cáo S5-HKD</p>
        <h1 className="text-2xl font-semibold text-gray-900">
          Sổ theo dõi nghĩa vụ thuế
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Tổng hợp thuế phải nộp, đã nộp và còn phải nộp theo kỳ
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Từ ngày *
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Đến ngày *
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
        </div>
        <div className="flex items-center justify-end">
          <button
            type="submit"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Áp dụng bộ lọc
          </button>
        </div>
      </form>

      {submitted && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-3">
              📊 Tổng hợp nghĩa vụ thuế
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* VAT Section */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700 border-b pb-1">
                  Thuế GTGT
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-600">Phải nộp:</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(summary.vat_payable)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-600">Đã nộp:</span>
                    <span className="text-sm font-semibold text-blue-700">
                      {formatCurrency(summary.vat_paid)}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-xs font-semibold text-gray-700">
                      Còn phải nộp:
                    </span>
                    <span
                      className={`text-sm font-bold ${
                        summary.vat_remaining > 0
                          ? "text-red-700"
                          : summary.vat_remaining < 0
                            ? "text-blue-700"
                            : "text-green-700"
                      }`}
                    >
                      {formatCurrency(summary.vat_remaining)}
                    </span>
                  </div>
                </div>
              </div>

              {/* PIT Section */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700 border-b pb-1">
                  Thuế TNCN
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-600">Phải nộp:</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(summary.pit_payable)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-600">Đã nộp:</span>
                    <span className="text-sm font-semibold text-blue-700">
                      {formatCurrency(summary.pit_paid)}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-xs font-semibold text-gray-700">
                      Còn phải nộp:
                    </span>
                    <span
                      className={`text-sm font-bold ${
                        summary.pit_remaining > 0
                          ? "text-red-700"
                          : summary.pit_remaining < 0
                            ? "text-blue-700"
                            : "text-green-700"
                      }`}
                    >
                      {formatCurrency(summary.pit_remaining)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs text-blue-800">
                💡 Thuế phải nộp được tự động tính từ S1. User chỉ nhập thuế đã
                nộp.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              Kết quả ( {submitted.from_date} → {submitted.to_date} )
            </h2>
            <p className="text-sm text-gray-500">
              {filteredRecords.length} bản ghi
            </p>
          </div>

          <AgGridTable
            rows={filteredRecords}
            columns={columns as any}
            defaultColDef={defaultColDef}
            pagination={false}
            autoHeight
          />
        </div>
      )}
    </div>
  );
};

export default S5Report;
